import { tracker } from '@lessonup/analytics';
import { logger, trackLessonActionWithOrigin } from '@lessonup/client-integration';
import { ContextMenuItemList, ContextMenuProvider, useShowContextMenu } from '@lessonup/teacher-modern';
import { Lesson } from '@lessonup/teaching-core';
import {
  ChannelTitle,
  Language,
  LessonTrigger,
  UserContext,
  Api,
  Explanation,
  IndexedChannelInfo,
  ClusterData,
  FacetMetaData,
} from '@lessonup/typescript-core/domain';
import { lessonRoute, editorUrl, editorV2Url } from '@lessonup/typescript-core/services';
import { MetaDataTagList, LessonPreviewThumbnail, Text } from '@lessonup/typescript-core/ui';
import { capitalize } from '@lessonup/typescript-core/utils';
import { IconDownload, IconLesson, IconMoreHorizontal, IconShare } from '@lessonup/ui-components';
import { guestimateDateFnsLocaleFromLanguage } from '@lessonup/utils';
import classNames from 'classnames';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { format } from 'date-fns/format';
import { formatDistanceStrict } from 'date-fns/formatDistanceStrict';
import { TFunction } from 'i18next';
import { intersection } from 'lodash';
import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AnyAction, followChannelAction, saveLessonAction } from '../../../redux/actions/actions';
import { isChannelFollowed } from '../../../redux/selectors';
import { AppServices } from '../../../services/AppServices';
import { htmlDecode } from '../../../utils/htmlDecode';
import ExplanationBox from '../../ExplanationBox/ExplanationBox';
import { useAppServices } from '../../appServices/AppServicesContext';
import ThumbBannerChannel from '../../banner/ThumbBannerChannel';
import './lessonSummaryCard.less';

const TRANSLATION_NS = 'lesson-summary-card';
const CONTEXT_MENU_ID = 'lesson-summary-card';

type LinkTo = (lessonId: string) => string;
export interface Props {
  className?: string;
  userRating?: number | undefined;
  isTip?: boolean;
  lesson: Lesson;
  channel?: IndexedChannelInfo | undefined;
  metaData?: FacetMetaData;
  /* Adds padding and hover effect */
  collapseCount?: ClusterData | undefined;
  inset?: boolean;
  explanation?: Explanation | undefined;
  linkTo?: LinkTo;
  columnClass?: string;
}

async function requestFollowIfRequired(
  trigger: LessonTrigger['trigger'],
  channelFollowed: boolean,
  channel: IndexedChannelInfo | undefined,
  services: AppServices,
  lesson: Lesson,
  dispatch: Dispatch<AnyAction>
): Promise<boolean> {
  if (channelFollowed) {
    return true;
  }

  if (channel && channel.name && channel.requireFollow) {
    const products = await services.user.currentUser()?.products;

    if (products) {
      const hasVoucherForChannel = products?.some((product) => product.channelId === channel._id);
      if (hasVoucherForChannel) return true;
    }

    const { success } = await services.channel.requireFollow({
      channelSlug: channel.name,
      channelTitle: channel.title && ChannelTitle.localizeTitle(channel.title),
      trigger: {
        lessonId: lesson._id,
        trigger,
        type: 'lesson',
      },
      userId: services.user.currentUser()?._id,
    });
    if (success) {
      dispatch(followChannelAction(channel.name));
    }
    return success;
  }

  return true;
}

export const lessonSummaryCardActions = (
  services: AppServices,
  lessonId: string,
  t: TFunction,
  dispatch: Dispatch<AnyAction>,
  user: UserContext | undefined,
  channelFollowed: boolean
): ContextMenuItemList<Lesson>[] => {
  return [
    {
      commandDescription: t('teach-lesson'),
      action: async () => {
        let lessonDetails: Api.LessonByIdResponse | undefined;
        try {
          lessonDetails = await services.lesson.lessonById(lessonId);
        } catch (error) {
          logger.error(error);
        }

        if (!lessonDetails) return;

        const { result } = lessonDetails;
        if (!result?.lesson) return;

        if (!services.user.currentUser()) {
          const loginResponse = await services.modals.open('loginPrompt', { forceNewTab: false, action: 'teach' });
          if (loginResponse.dismiss) return;
        }

        const requestChannelFollow = await requestFollowIfRequired(
          'play-lesson',
          channelFollowed,
          result.channel,
          services,
          result.lesson,
          dispatch
        );

        if (!requestChannelFollow) {
          return;
        }

        services.lesson.teach({
          lessonId,
          newTab: false,
          source: 'search',
          version: result.lesson.version ?? 'v1',
        });
      },
      svgIcon: IconLesson,
    },
    {
      commandDescription: t('save-lesson'),
      action: async () => {
        try {
          let lessonDetails: Api.LessonByIdResponse | undefined;
          try {
            lessonDetails = await services.lesson.lessonById(lessonId);
          } catch (error) {
            logger.error(error);
          }
          if (!lessonDetails) return;

          if (!services.user.currentUser()) {
            const loginResponse = await services.modals.open('loginPrompt', {
              forceNewTab: false,
              action: 'save',
            });
            if (loginResponse.dismiss) return;
          }
          const res = await services.lesson.saveLesson(lessonId);
          if (!res) return;

          tracker.events.lessonSaveForLater({ lessonId });
          trackLessonActionWithOrigin(
            'save-lesson',
            {
              lessonId,
              label: res.currentUser ? 'user' : 'non-user',
              channel: lessonDetails.result?.channel?.name,
            },
            lessonDetails.result?.lesson
          );

          if (!res.currentUser) return;

          dispatch(saveLessonAction(lessonId));
          services.modals.open('link', {
            title: t('lessonSavedTitle') || '',
            description: t('lessonSavedDescription'),
            link: lessonDetails.result?.lesson?.version === 'v2' ? editorV2Url(res.lessonId) : editorUrl(res.lessonId),
            linkText: t('openSavedLesson'),
          });
        } catch (error) {
          logger.error(error);
        }
      },
      svgIcon: IconDownload,
    },
    {
      commandDescription: t('share-lesson'),
      action: async () => {
        let lessonDetails: Api.LessonByIdResponse | undefined;
        try {
          lessonDetails = await services.lesson.lessonById(lessonId);
        } catch (error) {
          logger.error(error);
        }

        if (!lessonDetails) return;

        const { result } = lessonDetails;
        if (!result?.lesson) return;
        const { lesson, channel } = result;

        tracker.events.lessonShare({ lessonId, editorVersion: lesson.version ?? 'v1' });
        trackLessonActionWithOrigin('share-lesson', { lessonId, channel: channel?.name }, lesson);

        const shareWithStudents = async () => {
          if (lesson.channel && channel?.requireFollow) {
            const res = await services.channel.requireFollow({
              trigger: { trigger: 'handout-lesson', type: 'lesson', lessonId },
              channelSlug: lesson.channel,
              channelTitle: channel.title ? ChannelTitle.localizeTitle(channel.title) : undefined,
              userId: user?._id,
            });
            dispatch(followChannelAction(lesson.channel));
            if (!res.success) return;
          }

          try {
            const res = await services.lesson.saveLessonToRoot(lessonId);
            if (res) services.lesson.shareWithStudents(res.lessonId);
          } catch (error) {
            logger.error(error);
          }
        };

        const userProductIds = user?.products?.map((p) => p.productId) ?? [];
        const lessonProducts = lesson.products ?? [];
        const userHasAccessToProduct = intersection(userProductIds, lessonProducts).length > 0;
        services.modals.open('shareLesson', {
          url: services.config.shareLessonUrl(lessonId),
          isSignedIn: Boolean(services.user.currentUser()),
          hasProFeatures: userHasAccessToProduct || services.user.hasProFeatures(),
          canShareWithStudents: lesson.version !== 'v2',
          shareWithStudents,
        });
      },
      svgIcon: IconShare,
    },
  ];
};

const LessonSummaryCard: React.FunctionComponent<Props> = (props) => {
  const services = useAppServices();
  const { t: saveLessonModalTranslation } = useTranslation('lessonDetails');
  const dispatch = useDispatch();
  const [handleContextMenu] = useShowContextMenu<Lesson>();
  const { columnClass, inset = true, isTip, channel, lesson, explanation, collapseCount, linkTo } = props;
  const channelFollowed = useSelector(isChannelFollowed(channel && channel.name));
  const originalMetaData = props.metaData || FacetMetaData.fromLesson({ lesson });

  const metaData = {
    ...originalMetaData,
    subjects: originalMetaData.subjects.map((subj) => htmlDecode(subj) || ''),
  };

  const tipLabel = isTip ? (
    <span className="lesson-summary-card__tip-label">
      <Text translateKey="tip-label" options={{ ns: TRANSLATION_NS }} />
    </span>
  ) : (
    false
  );

  const onContextMenuClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleContextMenu(`${CONTEXT_MENU_ID}-${lesson._id}`, event, lesson, 'left');
  };

  const linkUrl = linkTo ? linkTo(lesson._id) : lessonRoute(lesson._id);
  return (
    <div
      className={classNames('base-summary-card', 'lesson-summary-card', { 'lesson-summary-card--no-inset': !inset })}
    >
      <ContextMenuProvider
        id={`${CONTEXT_MENU_ID}-${lesson._id}`}
        menuItems={lessonSummaryCardActions(
          services,
          lesson._id,
          saveLessonModalTranslation,
          dispatch,
          services.user.currentUser(),
          channelFollowed
        )}
        translationKey={TRANSLATION_NS}
      />
      <Link to={linkUrl} className={classNames('lesson-summary-card__inner', columnClass)}>
        <div className="lesson-summary-card__top">
          <div className="lesson-summary-card__thumbnail-container">
            <button
              className="lesson-summary-card__context-menu-button"
              onClick={onContextMenuClick}
              type="button"
              aria-label={saveLessonModalTranslation('open-lesson-menu')}
            >
              <IconMoreHorizontal />
            </button>
            <LessonPreviewThumbnail lesson={lesson} extraElement={tipLabel} />
          </div>
        </div>
        <div className="lesson-summary-card__bottom">
          <h3 className="lesson-summary-card__title">{htmlDecode(lesson.name) || ''}</h3>
          {lesson.pinCount && (
            <MetaInfo lastModifiedAt={lesson.modifiedDate || lesson.creationDate} count={lesson.pinCount} />
          )}
          <MetaDataTagList
            className="lesson-summary-card__tags"
            metaData={metaData}
            size="small"
            limit={5}
            version={lesson.version ?? 'v1'}
          />
        </div>
      </Link>
      <ThumbBannerChannel
        channel={channel}
        locale={metaData.country[0]}
        channelPublic={channel && channel.channelPublic}
      />
      {explanation && (
        <ExplanationBox explanation={explanation} collapseCount={collapseCount && collapseCount.size} id={lesson._id} />
      )}
    </div>
  );
};

type MetaInfoProps = { lastModifiedAt: Date; count: number };

const MetaInfo: React.FC<MetaInfoProps> = ({ lastModifiedAt: _lastModifiedAt, count }) => {
  const { t, i18n } = useTranslation(TRANSLATION_NS);
  const language = i18n.language as Language.Key;
  const lastModifiedAt = new Date(_lastModifiedAt);

  const slideCount = (
    <strong>
      {count} {t('slide', { count })}
    </strong>
  );

  return (
    <div className="lesson-summary-card__meta-info">
      <time
        className="lesson-summary-card__creation-date"
        dateTime={lastModifiedAt.toISOString()}
        suppressHydrationWarning
      >
        {lessonLastModifiedDate(lastModifiedAt, language)}
      </time>
      {' - '}
      {slideCount}
    </div>
  );
};

function lessonLastModifiedDate(lastModifiedAt: Date, language: Language.Key): string {
  const now = new Date();
  const isMoreThanMonthAgo = differenceInMonths(now, lastModifiedAt) >= 1;
  const locale = guestimateDateFnsLocaleFromLanguage(language);

  return isMoreThanMonthAgo
    ? capitalize(format(lastModifiedAt, 'MMMM yyyy', { locale }))
    : formatDistanceStrict(lastModifiedAt, now, { addSuffix: true, locale });
}

export default LessonSummaryCard;
